<template>
    <div class="flex flex-col gap-4 lg:flex-row lg:items-start">
        <!-- Rail: sticks below the app navbar while the page scrolls; on narrow
             stacked screens it collapses to just the section tabs + a toggle.
             The body wrapper below does the clipping so the rail itself can grow. -->
        <aside
            class="flex shrink-0 flex-col rounded-xl border border-gray-200 bg-white lg:sticky lg:top-[5.5rem] lg:h-[70vh] lg:max-h-none lg:w-[300px]"
            :class="railExpanded ? 'max-h-[55vh]' : ''"
        >
            <div class="flex items-center gap-1 border-b border-gray-200 p-2">
                <div class="min-w-0 flex-1">
                    <AiChatTabs />
                </div>
                <button
                    v-if="isNarrow"
                    type="button"
                    class="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                    :title="t('vue.ai_chat_toggle_list')"
                    @click="railOpen = !railOpen"
                >
                    <ChevronDownIcon
                        class="h-5 w-5 transition-transform"
                        :class="railOpen ? 'rotate-180' : ''"
                    />
                </button>
            </div>
            <div
                v-show="railExpanded"
                class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-b-xl"
            >
                <slot name="rail" />
            </div>
        </aside>

        <!-- Main: flows with the page — long content scrolls the whole page,
             not an inner panel. Pages pin their own sticky pieces (the chat
             pins its header and composer). -->
        <main class="mt-10 flex min-h-0 min-w-0 flex-1 flex-col lg:mt-0">
            <slot name="main" />
        </main>
    </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { ChevronDownIcon } from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import AiChatTabs from "@/Pages/AiChat/components/AiChatTabs.vue";

const { t } = useTranslations();

// Below this width the stacked rail is collapsed to its tabs by default.
const isNarrow = ref(false);
const railOpen = ref(false);
let mql: MediaQueryList | undefined;

const onMediaChange = (event: MediaQueryListEvent): void => {
    isNarrow.value = event.matches;
};

onMounted(() => {
    mql = window.matchMedia("(max-width: 849px)");
    isNarrow.value = mql.matches;
    mql.addEventListener("change", onMediaChange);
});

onBeforeUnmount(() => {
    mql?.removeEventListener("change", onMediaChange);
});

const railExpanded = computed(() => !isNarrow.value || railOpen.value);
</script>
